import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Bot,
  Search,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  Play,
  Pause,
  Calendar,
  MessageSquare,
  Settings,
  Plus,
  Link2
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { ConversationDetailModal } from '@/components/builder/ConversationDetailModal';

interface Chatbot {
  id: string;
  name: string;
  description: string;
  active: boolean;
  created_at: string;
  updated_at: string;
  conversation_count?: number;
  last_conversation_at?: string;
}

interface UserChatbotsSectionProps {
  userId: string;
}

export const UserChatbotsSection: React.FC<UserChatbotsSectionProps> = ({ userId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [isConversationModalOpen, setIsConversationModalOpen] = useState(false);

  // Fetch user's chatbots
  const { data: chatbots = [], isLoading, error } = useQuery({
    queryKey: ['userChatbots', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('chatbots')
        .select(`
          id,
          name,
          description,
          active,
          created_at,
          updated_at
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get conversation counts for each chatbot
      const chatbotsWithCounts = await Promise.all(
        data.map(async (bot) => {
          const { count, error: countError } = await supabase
            .from('conversations')
            .select('*', { count: 'exact', head: true })
            .eq('chatbot_id', bot.id);

          if (countError) {
            console.error('Error counting conversations for chatbot:', bot.id, countError);
            return { ...bot, conversation_count: 0 };
          }

          return { ...bot, conversation_count: count || 0 };
        })
      );

      return chatbotsWithCounts;
    },
  });

  // Filter chatbots based on search
  const filteredChatbots = chatbots.filter((bot: Chatbot) =>
    bot.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bot.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleViewChatbot = (chatbotId: string) => {
    // TODO: Navigate to chatbot details
    console.log('View chatbot:', chatbotId);
  };

  const handleEditChatbot = (chatbotId: string) => {
    // TODO: Navigate to chatbot edit
    console.log('Edit chatbot:', chatbotId);
  };

  const handleDeleteChatbot = (chatbotId: string) => {
    // TODO: Implement delete chatbot
    console.log('Delete chatbot:', chatbotId);
  };

  const handleViewConversation = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setIsConversationModalOpen(true);
  };

  const handleCloseConversationModal = () => {
    setIsConversationModalOpen(false);
    setSelectedConversationId(null);
  };

  const handleViewChatbotConversations = async (chatbotId: string) => {
    try {
      // Fetch the most recent conversation for this chatbot
      const { data: conversations, error } = await supabase
        .from('conversations')
        .select('id')
        .eq('chatbot_id', chatbotId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;

      if (conversations && conversations.length > 0) {
        // Open the most recent conversation
        handleViewConversation(conversations[0].id);
      } else {
        // No conversations found for this chatbot
        console.log('No conversations found for chatbot:', chatbotId);
        // TODO: Show a message or navigate to chatbot details
      }
    } catch (error) {
      console.error('Error fetching chatbot conversations:', error);
    }
  };

  const handleToggleActive = (chatbotId: string, active: boolean) => {
    // TODO: Implement toggle active status
    console.log('Toggle active:', chatbotId, !active);
  };

  const handleViewPingPost = (chatbotId: string) => {
    // Open the chatbot's portal builder page in a new tab
    // User can navigate to the Integrations tab to access PingPost settings
    const builderUrl = `/portal/builder/${chatbotId}`;
    window.open(builderUrl, '_blank');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (active: boolean) => {
    return (
      <Badge variant={active ? 'default' : 'secondary'}>
        {active ? 'Active' : 'Inactive'}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <span className="ml-2">Loading chatbots...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="text-center py-10">
          <p className="text-red-600">Error loading chatbots: {error.message}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <Bot className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{chatbots.length}</p>
                <p className="text-sm text-gray-600">Total Chatbots</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <Play className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold">
                  {chatbots.filter(bot => bot.active).length}
                </p>
                <p className="text-sm text-gray-600">Active Chatbots</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <MessageSquare className="h-8 w-8 text-purple-600" />
              <div>
                <p className="text-2xl font-bold">
                  {chatbots.reduce((total, bot) => total + (bot.conversation_count || 0), 0)}
                </p>
                <p className="text-sm text-gray-600">Total Conversations</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chatbots Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center space-x-2">
                <Bot className="h-5 w-5" />
                <span>User's Chatbots</span>
              </CardTitle>
              <CardDescription>All chatbots created by this user</CardDescription>
            </div>
           
          </div>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="flex items-center space-x-4 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search chatbots..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="text-sm text-gray-600">
              Showing {filteredChatbots.length} of {chatbots.length} chatbots
            </div>
          </div>

          {/* Table */}
          {filteredChatbots.length === 0 ? (
            <div className="text-center py-10">
              <Bot className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">
                {chatbots.length === 0 
                  ? "This user hasn't created any chatbots yet" 
                  : "No chatbots match your search criteria"
                }
              </p>
            </div>
          ) : (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Conversations</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Last Updated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredChatbots.map((chatbot: Chatbot) => (
                    <TableRow key={chatbot.id}>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Bot className="h-4 w-4 text-blue-600" />
                          <span className="font-medium">{chatbot.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-gray-600 max-w-xs truncate">
                          {chatbot.description || 'No description'}
                        </p>
                      </TableCell>
                      <TableCell>{getStatusBadge(chatbot.active)}</TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-1">
                          <MessageSquare className="h-4 w-4 text-gray-400" />
                          <span>{chatbot.conversation_count || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(chatbot.created_at)}</TableCell>
                      <TableCell>{formatDate(chatbot.updated_at)}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => handleViewChatbot(chatbot.id)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditChatbot(chatbot.id)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Chatbot
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleViewChatbotConversations(chatbot.id)}>
                              <MessageSquare className="h-4 w-4 mr-2" />
                              View Conversations
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleViewPingPost(chatbot.id)}>
                              <Link2 className="h-4 w-4 mr-2" />
                              View PingPost
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleToggleActive(chatbot.id, chatbot.active)}
                            >
                              {chatbot.active ? (
                                <>
                                  <Pause className="h-4 w-4 mr-2" />
                                  Deactivate
                                </>
                              ) : (
                                <>
                                  <Play className="h-4 w-4 mr-2" />
                                  Activate
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handleDeleteChatbot(chatbot.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conversation Detail Modal */}
      <ConversationDetailModal
        conversationId={selectedConversationId}
        isOpen={isConversationModalOpen}
        onClose={handleCloseConversationModal}
      />
    </div>
  );
};
